import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { LanguageService } from "../language-service";

import CEFRConstants from "./constants/CEFR.json";
import fieldOfActivityBakConstants from "./constants/field-of-activity-bak.json";
import industryBakConstants from "./constants/industry-bak.json";
import languageCodeConstants from "./constants/ISO3166-1_alpha3.json";
import languageConstants from "./constants/ISO639-1.json";
import phonePrefixConstants from "./constants/phone-prefixes.json";

import academicTitleConstants from "./constants/academic-title.json";
import activityTypeConstants from "./constants/activity-type.json";
import applicationStatusConstants from "./constants/application-status.json";
import benefitConstants from "./constants/benefit.json";
import careerLevelConstants from "./constants/career-level.json";
import companyTypeConstants from "./constants/company-type.json";
import documentTypeConstants from "./constants/document-type.json";
import employeeIntervalConstants from "./constants/employee-intervals.json";
import employmentTypeConstants from "./constants/employment-type.json";
import errorCodeConstants from "./constants/error-codes.json";
import feedbackTypeConstants from "./constants/feedback-type.json";
import feedbackViewConstants from "./constants/feedback-view.json";
import fieldOfActivityConstants from "./constants/field-of-activity.json";
import industryConstants from "./constants/industry.json";
import languageLevelConstants from "./constants/language-level.json";
import mailCategoryConstants from "./constants/mail-category.json";
import notificationTypeConstants from "./constants/notification-type.json";
import paymentIntervalConstants from "./constants/payment-interval.json";
import salaryIntervalConstants from "./constants/salary-interval.json";
import salutationConstants from "./constants/salutation.json";
import skillLevelConstants from "./constants/skill-level.json";
import stepCategoryConstants from "./constants/step-category.json";
import userRoleConstants from "./constants/user-role.json";
import workExperienceConstants from "./constants/work-experience.json";
import workingHoursConstants from "./constants/working-hours.json";

export interface Constant {
  label: string;
  value: number | string;
  sub?: Constants;
}

export interface Constants {
  [key: string]: Constant;
}

export interface LanguageConstant {
  code: string;
  name: string;
  nativeName: string;
  germanName: string;
}

export interface LanguageCodeConstant {
  code: string;
  name: string;
}

export interface IndustryBakConstant {
  germanName: string;
}

export interface FieldOfActivityBakConstant {
  germanName: string;
}

export interface PhonePrefixConstant {
  prefix: string;
  country: string;
}

export interface CEFRConstant {
  code: string;
  name: string;
  germanName: string;
}

@Injectable({
  providedIn: 'root',
})
export class ConstantsService {
  private languageService = inject(LanguageService);

  public readonly CEFRConstants: CEFRConstant[] = CEFRConstants;
  public readonly fieldOfActivityBakConstants: FieldOfActivityBakConstant[] =
    fieldOfActivityBakConstants;
  public readonly industryBakConstants: IndustryBakConstant[] =
    industryBakConstants;
  public readonly languageCodeConstants: LanguageCodeConstant[] =
    languageCodeConstants;
  public readonly languageConstants: LanguageConstant[] = Object.values(
    languageConstants
  ).map((languageConstant, index) => {
    return {
      ...languageConstant,
      ...{ code: Object.keys(languageConstants)[index] },
    };
  });
  public readonly phonePrefixConstants: PhonePrefixConstant[] =
    phonePrefixConstants;
  public readonly academicTitleConstants: Constants = academicTitleConstants;
  public readonly activityTypeConstants: Constants = activityTypeConstants;
  public readonly applicationStatusConstants: Constants =
    applicationStatusConstants;
  public readonly careerLevelConstants: Constants = careerLevelConstants;
  public readonly companyTypeConstants: Constants = companyTypeConstants;
  public readonly documentTypeConstants: Constants = documentTypeConstants;
  public readonly employeeIntervalConstants: Constants =
    employeeIntervalConstants;
  public readonly employmentTypeConstants: Constants = employmentTypeConstants;
  public readonly errorCodeConstants: Constants = errorCodeConstants;
  public readonly feedbackTypeConstants: Constants = feedbackTypeConstants;
  public readonly feedbackViewConstants: Constants = feedbackViewConstants;
  public readonly fieldOfActivityConstants: Constants =
    fieldOfActivityConstants;
  public readonly industryConstants: Constants = industryConstants;
  public readonly languageLevelConstants: Constants = languageLevelConstants;
  public readonly mailCategoryConstants: Constants = mailCategoryConstants;
  public readonly notificationTypeConstants: Constants =
    notificationTypeConstants;
  public readonly paymentIntervalConstants: Constants =
    paymentIntervalConstants;
  public readonly salaryIntervalConstants: Constants = salaryIntervalConstants;
  public readonly salutationConstants: Constants = salutationConstants;
  public readonly skillLevelConstants: Constants = skillLevelConstants;
  public readonly stepCategoryConstants: Constants = stepCategoryConstants;
  public readonly userRoleConstants: Constants = userRoleConstants;
  public readonly workExperienceConstants: Constants = workExperienceConstants;
  public readonly workingHoursConstants: Constants = workingHoursConstants;
  public readonly benefitConstants: Constants = benefitConstants;

  getObjectValues(obj: object): any[] {
    return Object.values(obj);
  }

  getLabelValueArray(constants: Constants): Constant[] {
    return Object.values(constants);
  }

  getValueByIndex(constants: Constants, index: string | number): any {
    return (
      this.getLabelValueArray(constants)[Number(index)]?.value ?? undefined
    );
  }

  getLabelByIndex(constants: Constants, index: string | number): any {
    return (
      this.getLabelValueArray(constants)[Number(index)]?.value ?? undefined
    );
  }

  getLabelByValue(constants: Constants, value: string | number) {
    return (
      this.getLabelValueArray(constants).find(
        (constant) => constant.value === value
      )?.label ?? undefined
    );
  }

  getLanguageByCode(code: string) {
    return this.languageConstants.find(
      (languageConstant) => languageConstant.code === code
    );
  }

  getLanguageName(languageConstant: LanguageConstant | undefined): string {
    if (!languageConstant) {
      return '';
    }
    return this.languageService.currentLanguage.value === 'de-DE'
      ? languageConstant.germanName
      : languageConstant.name;
  }

  getFullName(
    academicTitle: number,
    title: number,
    firstName: string,
    lastName: string
  ) {
    return new Observable((subscriber) => {
      this.languageService
        .getTranslateService()
        .selectTranslate([
          this.getLabelByValue(this.salutationConstants, title) ?? '',
          this.getLabelByValue(this.academicTitleConstants, academicTitle) ??
            '',
        ])
        .subscribe({
          next: (result) => {
            const resultValues = Object.values(result);
            subscriber.next(
              `${
                resultValues[0] && resultValues[0] !== '-'
                  ? `${resultValues[0]} `
                  : ''
              }${
                resultValues[1] && resultValues[1] !== '-'
                  ? `${resultValues[1]} `
                  : ''
              }${firstName ? `${firstName} ` : ''}${
                lastName ? `${lastName} ` : ''
              }`
            );
          },
        });
    });
  }
}
